import { Injector } from "@angular/core";
import { Validators } from "@angular/forms";

import { ActionButton } from "../../../../components/buttons/gloss-button/actionButton";
import { Balances } from "../../../../models/enum/balance.enum";
import { InputTypes } from "../../../../models/enum/inputTypes.enum";
import { TransactionStatusEnum } from "../../../../models/enum/transactionType";
import { FormInterface } from "../../../../models/interfaces/form.interface";
import { GlossInputOptions, isOfType } from "../../../../models/types/general-types";
import { BaseForm } from "../../../../shared/form/BaseForm";
import { HelperNumericInput } from "../../../../shared/utils/helper-numeric-input";
import { BookBalance } from "@bitwarden/web-vault/app/shared/utils/helper.account/balance";
import {
  actionButtonOptions,
  priceInputOptions,
  defaultInputOptions,
} from "../accounts-add-edit/component.options";
import {
  AllowedCurrencies,
  CurrencyType,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import {
  BalanceForm as BalanceFormFields,
  dialogDataType,
} from "@bitwarden/web-vault/app/models/types/account.types";

export class BalanceForm extends BaseForm implements FormInterface {
  data: dialogDataType;
  injector: Injector;
  submitButton: () => void;

  // balanceInputOptions: GlossInputOptions;
  // timeInputOptions: GlossInputOptions;
  // descriptionInputOptions: GlossInputOptions;
  saveButtonOptions: ActionButton;
  selectedBalance: string;

  balances = [
    {
      label: this.i18nService.t("closingBalance"),
      value: Balances.closing,
      selected: true,
      labelType: "text",
    },
    {
      label: this.i18nService.t("openingBalance"),
      value: Balances.opening,
      selected: false,
      labelType: "text",
    },
  ];

  constructor(injector: Injector, data: dialogDataType, submitButton: () => void) {
    super(injector);
    this.data = data;
    this.injector = injector;
    this.submitButton = submitButton;
    this.initialize();
  }

  initialize() {
    this.selectedBalance =
      this.data?.initialType === Balances.opening ? Balances.opening : Balances.closing;

    if (this.data?.initialCurrency) {
      // Stock symbols (e.g. AAPL) are valid here but not in AllowedCurrencies, so skip isOfType guard.
      this.currency = this.data.initialCurrency as CurrencyType;
    } else if (isOfType<CurrencyType>(this.data?.accountView?.currency, AllowedCurrencies)) {
      this.currency = this.data?.accountView?.currency;
    } else {
      this.currency = this.getCurrencyFromLocation();
    }

    this.title = this.i18nService.t("addBalance");

    // Use initial values if provided, otherwise use defaults
    const initialBalance = this.data?.initialBalance ?? null;
    const initialDate =
      this.data?.initialDate ?? this.dateFormat.getDateStringFromStamp(new Date().getTime());
    const initialDescription = this.data?.initialDescription ?? null;
    // console.log("initialBalance in form", initialBalance);
    // console.log("initialDate in form", initialDate);

    this.formGroup = this.formBuilder.group({
      currency: [this.currency, Validators.required],
      balance: [initialBalance, [HelperNumericInput.isNumberValidator, Validators.required]],
      date: [initialDate, Validators.required],
      time: [null],
      description: [initialDescription],
      type: this.selectedBalance,
      transactionStatus: TransactionStatusEnum.manual_balance,
    });

    // this.balanceInputOptions = {
    //   ...priceInputOptions,
    //   isRequired: true,
    //   type: InputTypes.number,
    //   label: this.i18nService.t("balance"),
    //   onInput: (event: Event) => this.onInput(event, "balance"),
    //   inputBlurred: () => this.clearDefaultValue("balance"),
    // };
    //
    // this.timeInputOptions = {
    //   ...defaultInputOptions,
    //   label: this.i18nService.t("time"),
    //   placeholder: "00:00:00",
    //   isRequired: false,
    //   type: InputTypes.time,
    //   onInput: (event: Event) => this.onInput(event, "time"),
    // };

    // currently the search function will work only with the description for the balance transaction
    // this.descriptionInputOptions = {
    //   ...defaultInputOptions,
    //   label: this.i18nService.t("description"),
    //   placeholder: this.i18nService.t("description"),
    //   onInput: (event: Event) => this.onInput(event, "description"),
    //   isRequired: false,
    // };

    this.saveButtonOptions = new ActionButton({
      ...actionButtonOptions,
      onClick: this.submitButton.bind(this),
    });
  }

  selectBalance(value: string) {
    this.selectedBalance = value;
    this.formGroup.controls.type.setValue(value);
  }

  resetForm() {
    this.saveButtonOptions.enableButton(true);
    this.loading = false;
  }

  async submit() {
    this.loading = true;

    if (!this.formGroup.valid) {
      this.resetForm();
      return;
    }

    const balanceForm = this.formGroup.value;
    // choose the correct transaction status for the balance transaction if it is set to opening or closing
    balanceForm.transactionStatus = this.getBalanceDefinition(balanceForm);
    this.setDefaultTime(balanceForm);
    const balance = new BookBalance(this.data.accountView, balanceForm, this.injector);
    const result = await balance.process();

    if (result) {
      this.data.closeDialog();
      this.loading = false;
    } else {
      this.resetForm();
    }
  }

  getBalanceDefinition(balanceForm: BalanceFormFields): string {
    if (balanceForm.type === "Opening") {
      return TransactionStatusEnum.opening;
    } else if (balanceForm.type === "Closing") {
      return TransactionStatusEnum.closing;
    } else {
      return balanceForm.transactionStatus;
    }
  }

  setDefaultTime(balanceForm: any) {
    if (
      balanceForm.time === null &&
      balanceForm.transactionStatus === TransactionStatusEnum.closing
    ) {
      balanceForm.time = "23:59:59";
    }
    if (
      balanceForm.time === null &&
      balanceForm.transactionStatus === TransactionStatusEnum.opening
    ) {
      balanceForm.time = "00:00:00";
    }
  }
}
